import { Mic, X, Check, Loader2 } from "lucide-react"
import { useTheme } from "components/core/theme-provider"
import useAudioRecorder from "../hooks/use-audio-recorder";
import { useSpeechToText } from "../hooks/use-speech-to-text";
import { TranscribeAudioToTextResponse } from "../types/model.types";
// import { useCallback, useEffect, useRef } from "react";

interface DictateButtonProps {
    onTranscribe: (content: string) => void;
    setIsDictationOnGoing: (isDictationOnGoing: boolean) => void;
    isDictationOnGoing: boolean;
}

export const DictateButton = ({ onTranscribe, setIsDictationOnGoing, isDictationOnGoing }: DictateButtonProps) => {
    const { theme } = useTheme();
    const { isRecording, startRecording, stopRecording, cancelRecording, canvasRef } = useAudioRecorder();
    const { mutateAsync: transcribeAudioToText, isPending: isTranscribing } = useSpeechToText();

    const handleStartRecording = async () => {
        try {
            await startRecording();
            setIsDictationOnGoing(true);
        } catch (error) {
            console.log(error);
            setIsDictationOnGoing(false);
        }
    }

    const handleStopRecording = async () => {
        if (!isRecording) return;

        try {
            const audioBlob = await stopRecording() as Blob | null;
            if (audioBlob) {
                const res = await transcribeAudioToText(audioBlob) as TranscribeAudioToTextResponse;
                console.log(res);
                setIsDictationOnGoing(false);
                if (res) {
                    onTranscribe?.(res.text);
                } else {
                    console.warn('Transcription response missing text or has error:', res);
                }
            }
        } catch (error) {
            console.error('Error in handleStopRecording:', error);
            setIsDictationOnGoing(false);
        }
    }

    const handleCancelRecording = () => {
        setIsDictationOnGoing(false);
        cancelRecording();
    }

    if (isDictationOnGoing) {
        return (
            <div className="flex w-full gap-2 items-center">
                <button
                    aria-pressed="true"
                    aria-label="Dictate button"
                    className={`flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} shadow-sm hover:bg-muted disabled:opacity-40 cursor-default`}
                    disabled={isTranscribing}
                >
                    <Mic size={16} className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </button>
                <div className="flex-1">
                    <canvas ref={canvasRef} height={32} className="w-full rounded" />
                </div>

                <button
                    aria-pressed="true"
                    aria-label="Cancel recording button"
                    className={
                        `flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} shadow-sm hover:bg-muted disabled:opacity-40`
                    }
                    disabled={isTranscribing}
                    onClick={handleCancelRecording}
                >
                    <X size={16} className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </button>

                {!isTranscribing && <button
                    aria-pressed="true"
                    aria-label="Stop recording button"
                    className={
                        'flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] border-primary-600 shadow-sm hover:bg-muted disabled:opacity-30 '
                        // + (theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200')
                    }
                    onClick={handleStopRecording}
                >
                    <Check size={16} className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </button>}
                {isTranscribing && <button
                    aria-pressed="true"
                    aria-label="Transcribing loader button"
                    className={
                        `flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} shadow-sm hover:bg-muted disabled:opacity-30`
                    }
                >
                    <Loader2 className={`animate-spin ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </button>}
            </div>
        )
    }

    return (
        <button
            aria-pressed="false"
            aria-label="Dictate button"
            className={`flex h-[34px] min-w-[34px] items-center justify-center sm:p-2 p-1 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} shadow-sm hover:bg-muted disabled:opacity-40 ${isRecording ? 'bg-primary-50 animate-pulse-glow border-transparent' : ''}`}

            onClick={handleStartRecording}
            disabled={isTranscribing}
        >
            <Mic size={16} className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
        </button>)
}